import useTranslation from 'next-translate/useTranslation';
import { useRouter } from 'next/router';
import React, { useEffect, useRef, useState } from 'react';

import { WeeeIcon } from '@/components/common/Icon';

import { CmsComponentProps } from '@/types/cms';
import { getSearchHotKeywords } from '@/api/item';
import Swiper, { SwiperSlide } from '@/components/common/Swiper';
import useClickActionT2 from '@/tracks/useClickActionT2';
import useSticky from '@/hooks/useSticky';
import useHomepage from '@/store/home';
import { twMerge } from 'tailwind-merge';

export interface Props {
  onSearch?: (any) => void;
  onFocus?: (any) => void;
  targetUrl?: string;
  isSupportHotKeyword?: boolean;
}

type SearchPropsType = Props & CmsComponentProps;

const Search: React.FC<SearchPropsType> = props => {
  const router = useRouter();
  const targetRef = useRef(null);
  const {
    properties,
    targetUrl = '/search',
    cmsComponentKey,
    componentPos,
    isSupportHotKeyword = true,
    stickyHeight = 35
  } = props;
  const [hotKeywords, setHotKeywords] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchBarTop, setSearchBarTop] = useState(48);
  const swiperIndex = useRef(0);
  const { buttonClick } = useClickActionT2();
  const { store } = useHomepage();

  const eventKey = properties?.event_key || cmsComponentKey;

  const { eleRef } = useSticky({
    scrollHeight: stickyHeight,
    targetRef: targetRef
  });

  useEffect(() => {
    if (!isSupportHotKeyword) {
      setLoading(false);
      return;
    }
    const getHotKeyword = async () => {
      const res = await getSearchHotKeywords();
      setLoading(false);
      if (res?.result) {
        setHotKeywords(res.object?.keywords || []);
      }
    };
    getHotKeyword();
  }, [isSupportHotKeyword]);

  useEffect(() => {
    const handleTimeBannerChange = e => {
      const nextTop = e?.detail?.visible ? 102 : 48;
      setSearchBarTop(pre => (pre === nextTop ? pre : nextTop));
    };
    window.addEventListener('new-user-banner-change', handleTimeBannerChange);
    return () => {
      window.removeEventListener('new-user-banner-change', handleTimeBannerChange);
    };
  }, []);

  const handleOnSlideChange = swiper => {
    swiperIndex.current = swiper.realIndex;
  };

  const renderHotKeywordsSwiper = () => {
    return (
      <Swiper
        aria-label="search bar"
        onSlideChange={handleOnSlideChange}
        loop={true}
        className="w-full h-full !ml-[50px]"
        direction="vertical"
        autoplay={{ delay: 3000 }}>
        {hotKeywords.map((item, index) => {
          return (
            <SwiperSlide
              aria-label={item}
              className="!w-auto flex items-center justify-start text-sm leading-[17px] mr-2 p-0 rounded-[100px]"
              key={index}>
              <div className="max-w-full overflow-hidden text-ellipsis whitespace-nowrap text-left text-surface-100-fg-default !font-medium">
                {item}
              </div>
            </SwiperSlide>
          );
        })}
      </Swiper>
    );
  };

  const trackHotkeyword = (e, setKeywordToStorage?: boolean) => {
    e?.stopPropagation?.();
    buttonClick({
      co: {
        target_nm: 'search',
        click_type: 'view',
        target_type: 'normal_button'
      },
      ctx: {
        page_target: hotKeywords[swiperIndex.current]
      }
    });
    if (setKeywordToStorage) {
      //没有热词的时候，点击搜索框，跳转到搜索页, 而非结果页面
      router.push(`${targetUrl}?_hotkeyword=${encodeURIComponent(hotKeywords[swiperIndex.current])}`);
    } else {
      router.push(`${targetUrl}?keyword=${encodeURIComponent(hotKeywords[swiperIndex.current])}&from=hotkeyword`);
    }
  };

  return (
    <>
      <div
        ref={targetRef}
        data-role="roleForRecommendBanner"
        id="homepage-search-bar"
        data-testid="wid-search-bar"
        style={{
          background: store?.bannerTheme?.color,
          top: `${searchBarTop}px`
        }}
        className="sticky z-[100] opacity-100 pt-1.5 pb-2 px-4 inset-x-0 bg-surface-100-bg">
        <style jsx>{`
          #homepage-search-bar.recommend-tab-fixed {
            opacity: 0;
          }
        `}</style>
        <div
          id="homepage-search-bar-input"
          data-testid="wid-search-bar-input"
          role="button"
          title="search"
          className={twMerge(
            'w-full bg-surface-100-bg shadow-[0px_9px_20px_rgba(0,0,0,0.07),0px_1px_8px_rgba(0,0,0,0.04)] border-[0.5px] border-solid border-surface-100-hairline h-[41px] flex items-center rounded-full relative box-border',
            store?.bannerTheme?.color && 'border-none shadow-none'
          )}
          onClick={e => {
            //没有热词的时候，点击搜索框，跳转到搜索页
            if (hotKeywords.length) {
              trackHotkeyword(e, true);
            } else {
              buttonClick({
                mod_nm: eventKey,
                mod_pos: componentPos,
                co: {
                  target_nm: 'search_bar',
                  click_type: 'view',
                  target_type: 'normal_button'
                }
              });
              router.push(targetUrl);
            }
          }}>
          <WeeeIcon
            aria-labelledby="homepage-search-bar-input"
            type="iconcategory_select"
            className="!text-[20px] text-surface-100-fg-minor absolute left-4 !font-normal"
          />
          {loading ? null : hotKeywords.length > 0 ? (
            renderHotKeywordsSwiper()
          ) : (
            <div
              role="button"
              aria-label={properties.tips}
              className="text-surface-100-fg-minor text-center flex-1 mx-1.5 enki-body-sm-medium">
              {properties.tips}
            </div>
          )}
          {hotKeywords.length > 0 && (
            <div
              className="shrink-0 w-7 h-7 flex items-center justify-center bg-reserved-true-black font-normal text-reserved-true-white py-0 mr-1.5 rounded-full"
              onClick={trackHotkeyword}>
              <WeeeIcon
                aria-label="search"
                role="button"
                type="iconrightarrow"
                style={{ fontSize: 28, lineHeight: 1 }}
              />
            </div>
          )}
        </div>
      </div>
      <div ref={eleRef} id="search-bar-sticky" />
    </>
  );
};

export default Search;
